/**
 * The per-field-type configuration this round adds: their enums, their defaults, and the
 * numeric ranges the sliders and the storage parser both clamp to.
 *
 * Ranges live here, once, so the control that offers a value and the parser that
 * accepts one cannot disagree about what is in range (spec R70, threat model,
 * abuse case 2).
 */

import { CtaConfiguration, DescriptionStyle } from '../types';

/** The Short Description's three styles, in the prototype's order (spec R71). */
export const DESCRIPTION_STYLES: DescriptionStyle[] = ['partly', 'full', 'collapsed'];

/** "Clamp 2 lines" — the prototype's `descStyle: 'partly'` default (spec R71). */
export const DEFAULT_DESCRIPTION_STYLE: DescriptionStyle = 'partly';

/** The whitelist every untrusted read of `descStyle` goes through. */
export const isDescriptionStyle = (candidate: unknown): candidate is DescriptionStyle =>
  DESCRIPTION_STYLES.some((style) => style === candidate);

/** A slider's bounds — `min`, `max` and `step` as one value (spec R70). */
export interface NumericRange {
  min: number;
  max: number;
  step: number;
  fallback: number;
}

/** "Button width — <n>%": 30 to 100 in steps of 5, defaulting to 60 (spec R70). */
export const CTA_WIDTH_PCT_RANGE: NumericRange = { min: 30, max: 100, step: 5, fallback: 60 };

/** "Button height — <n>px": 30 to 56 in steps of 2, defaulting to 34 (spec R70). */
export const CTA_HEIGHT_PX_RANGE: NumericRange = { min: 30, max: 56, step: 2, fallback: 34 };

export const MAX_CTA_LABEL_LENGTH = 40;

/**
 * Into range and onto a step, or the documented default.
 *
 * A stored `1e9`, a `NaN`, or a value between two steps all resolve to something
 * the slider can actually represent, so the control and the card never disagree.
 */
export const clampToRange = (value: unknown, range: NumericRange): number => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return range.fallback;
  }
  const bounded = Math.min(range.max, Math.max(range.min, value));
  const steps = Math.round((bounded - range.min) / range.step);
  return Math.min(range.max, range.min + steps * range.step);
};

/**
 * The CTA's starting options (spec R70): the prototype's own defaults — text on,
 * icon off, fixed 60% width, 34px tall.
 *
 * `label` starts empty so the preview falls back to the product's own CTA label and
 * the input shows its "Add to Cart" placeholder rather than pre-filled text.
 */
export const DEFAULT_CTA_CONFIGURATION: CtaConfiguration = {
  label: '',
  showText: true,
  showIcon: false,
  fullWidth: false,
  widthPct: CTA_WIDTH_PCT_RANGE.fallback,
  heightPx: CTA_HEIGHT_PX_RANGE.fallback,
};
